import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { ChatButton } from '../../components/common/ChatButton'
import { CallButton } from '../../components/common/CallButton'
import useProductCategories from '../../hooks/useProductCategories'
import { CHAT_CONTEXT_TYPES, CONTENT_TYPES } from '../../constants/contentTypes'
import USER_TYPE from '../../constants/userType'
import BackButtonAndTitle from '../../components/retailer-shop/BackButtonAndTitle'
import DisplayShopName from '../../components/common/DisplayShopName'
import DisplayBidStatus from '../../components/common/DisplayBidStatus'
import CloseProductRequestModal from '../../components/common/CloseProductRequestModal'
import ShopLocation from '../../components/common/ShopLocation'
import moment from 'moment'
import CountdownTimer from '../../components/CountdownTimer'
import { MESSAGE_REFETCH_INTERVAL_MILLIS } from '../../constants/messagingConstants'
import ReportButton from '../../components/common/ReportButton'
import SeoHelmet from '../../components/common/SeoHelmet'

const BidDetailsPage = () => {
  const { getCategoryNameById } = useProductCategories()
  const { bidId } = useParams()
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false)

  const { data: bidData } = useQuery({
    queryKey: QueryKeys.bidDetails(Number(bidId)),
    queryFn: async () => await handleGetRequest(`/customers/bids/request/${bidId}`),
    refetchInterval: (queryData) => {
      const bid = queryData?.state?.data?.data || {}
      return bid?.isOpen ? MESSAGE_REFETCH_INTERVAL_MILLIS : false
    },
  })

  const bid = bidData?.data

  if (!bid?.id) {
    return null
  }
  const isBid = !bid.productFinderId
  const isExpired = moment().isAfter(moment(bid.expiresAt))

  const renderClosingTime = () => {
    if (!bid?.isOpen) return null
    if (!bid?.expiresAt) return null

    const expiryTimestamp = moment(bid.expiresAt).valueOf()

    if (isExpired) {
      return
    }

    return (
      <div className="flex gap-4 items-center mt-2">
        <span className="text-[16px] text-black/70 font-normal">Closing in</span>
        <CountdownTimer expiryTimestamp={expiryTimestamp} />
      </div>
    )
  }

  const renderBidResponse = (bidResponse) => {
    return (
      <div
        key={bidResponse.id}
        className="flex flex-col w-full bg-background border border-secondary-300 rounded-[10px] px-4 py-3 mb-4"
      >
        <div className="flex flex-row">
          <div className="h-max block rounded-[10px] overflow-hidden bg-neutral-200-100 mr-4">
            <img
              src={bidResponse.imageUrl}
              alt={bidResponse.productName}
              className="w-[100px] rounded-[10px]  aspect-square object-cover"
            />
          </div>
          <div className="flex flex-col justify-center flex-1">
            {bidResponse.productName && (
              <Link
                to={`/retailers/${bidResponse.shopSlug}/products/${bidResponse.productSlug}`}
                className="text-blue-600 hover:underline"
              >
                <div className="text-lg  leading-tight">{bidResponse.productName}</div>
              </Link>
            )}
            <div className="text-md text-gray-500 mt-1">
              {bidResponse.productName ? (
                <DisplayShopName
                  shop={{
                    name: bidResponse.shopName,
                    isPremium: bidResponse.isShopPremium,
                    isVerified: bidResponse.isShopVerified,
                  }}
                />
              ) : (
                <Link
                  to={`/retailers/${bidResponse.shopSlug}/products`}
                  className="text-blue-600 hover:underline"
                >
                  <DisplayShopName
                    shop={{
                      name: bidResponse.shopName,
                      isPremium: bidResponse.isShopPremium,
                      isVerified: bidResponse.isShopVerified,
                    }}
                  />
                </Link>
              )}
            </div>
            <ShopLocation
              shop={{
                shopNumber: bidResponse.shopNumber,
                shopFloor: bidResponse.shopFloor,
                shopBuildingName: bidResponse.shopBuildingName,
              }}
              className="mt-1"
            />
            <div className="font-bold text-md text-gray-700 mt-1">
              Bid Price: <span className="text-red-500">{bidResponse.bidPriceText}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <ChatButton
            chatConfig={{
              targetId: bidResponse.shopId,
              contextType: !isBid ? CHAT_CONTEXT_TYPES.productFinder : CHAT_CONTEXT_TYPES.bidding,
              contextId: bidResponse.id,
              targetUserType: USER_TYPE.retailer,
              description: (
                <>
                  Contact the retailer for more details about{' '}
                  <strong>{bidResponse.productName}</strong>. The retailer has offered a bid price
                  of <strong>{bidResponse.bidPriceText}</strong>.
                </>
              ),
            }}
          >
            Chat With Retailer
          </ChatButton>
          <CallButton
            targetPhoneNumber={bidResponse.shopPhoneNumber}
            onClick={() => {}}
            label="Call Retailer"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="main-container  min-h-screen relative">
      <SeoHelmet
        title={isBid ? 'Bid Details' : 'Product Finder Details'}
        description={
          isBid
            ? `View details of bid request ${bid.id} including product information, retailer bids, and status.`
            : `View details of product finder request ${bid.productFinderId} including product information, retailer responses, and status.`
        }
        keywords={
          isBid
            ? `bid details, bid request ${bid.id}, retailer bids, product bidding`
            : `product finder details, product finder request ${bid.productFinderId}, retailer responses, product search`
        }
        url={`/bids/${bidId}`}
      />
      <div className="flex justify-between items-center mb-4">
        <BackButtonAndTitle title={isBid ? 'Product Bidding Details' : 'Product Finder Details'} />
        {!isBid && (
          <ReportButton
            contentId={bid.productFinderId}
            contentType={CONTENT_TYPES.productFinder}
            reportTarget={'Product Finder Request'}
          />
        )}
      </div>
      <div className=" mx-auto bg-background border  rounded-[10px]  items-center px-4 py-3 mb-6">
        <img
          src={bid?.imageUrl}
          alt={bid.productName || getCategoryNameById(bid.productFinderCategoryId)}
          className="w-full  aspect-square h-44 rounded-[10px] object-contain mb-3"
        />
        <div>
          <div>
            <div className="flex items-center mt-0.5 justify-between">
              <span className="text-[18px] text-foreground-secondary font-medium font-fontFamily">
                {bid.productName || getCategoryNameById(bid.productFinderCategoryId)}
              </span>
              <div>
                <DisplayBidStatus isOpen={bid.isOpen} expiresAt={bid.expiresAt} />
                <span className="text-[15px] font-medium italic text-foreground-secondary bg-orange-50 px-2 py-1 rounded ml-2">
                  {bid.productFinderId ? `FND-${bid.productFinderId}` : `BID-${bid.id}`}
                </span>
              </div>
            </div>
            <div className="mb-3">
              {!bid?.isOpen && bid?.closingReason && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <div className="text-sm text-red-600">
                    <span className="font-medium">Closing Reason:</span> {bid.closingReason}
                  </div>
                </div>
              )}
            </div>
            <div className="text-[15px] text-gray-400 mt-1 ">
              {bid.productDescription || bid.productFinderDescription}
            </div>
            <div className="text-[15px] font-normal text-gray-700 mt-2">
              {bid?.requestQuantity} Request Quantity
            </div>
            {renderClosingTime()}
          </div>
        </div>
      </div>

      <div className=" mx-auto">
        <div className="flex items-center justify-between mb-3 w-full">
          <h3 className="text-[16.9778px] font-normal text-[rgba(0,0,0,0.78)]">
            Retailer bids ({bid.bidResponses?.length})
          </h3>
        </div>
        {bid.bidResponses?.length > 0 ? (
          <div className="space-y-6">
            {bid.bidResponses.map((response) => renderBidResponse(response))}
          </div>
        ) : (
          <div className="text-center py-8 px-4 border border-dashed border-gray-300 rounded-lg bg-background">
            <p className="text-gray-500">No retailer bids received for this item yet.</p>
            <p className="text-sm text-gray-400 mt-1">
              Check back later or contact support if you expected responses.
            </p>
          </div>
        )}
      </div>
      {bid.isOpen && !isExpired ? (
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-gray-200 p-4 flex justify-between items-center max-w-2xl mx-auto">
          <span className="text-sm text-gray-600">
            {isBid ? 'Bid request' : 'Product finder'} is open for responses.
          </span>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            onClick={() => setIsCloseModalOpen(true)}
          >
            Close
          </button>
        </div>
      ) : null}

      <CloseProductRequestModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        request={bid}
        onSuccessCallback={() => {
          setIsCloseModalOpen(false)
        }}
      />
    </div>
  )
}

export default BidDetailsPage
